import React from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  Chip,
  Divider,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InfoIcon from '@mui/icons-material/Info';
import { PessoaSemPix } from '../services/api';
import { colors } from '../theme/expertiseTheme';

/**
 * Componente de alerta para pessoas sem chave PIX
 */
type Props = {
  pessoasSemPix: PessoaSemPix[];
};

const AlertaPessoasSemPix: React.FC<Props> = ({ pessoasSemPix }) => {
  if (!pessoasSemPix || pessoasSemPix.length === 0) {
    return null;
  }

  const formatarValor = (valor: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(Math.abs(valor));
  };

  const formatarData = (data: string) => {
    return new Date(`${data}T00:00:00`).toLocaleDateString('pt-BR');
  };

  return (
    <Alert
      severity="warning"
      icon={<WarningAmberIcon fontSize="large" />}
      sx={{ 
        mb: 3,
        borderRadius: '16px',
        backgroundColor: 'rgba(245, 158, 11, 0.08)',
        border: '1px solid rgba(245, 158, 11, 0.2)',
        '& .MuiAlert-icon': {
          color: '#F59E0B',
        },
      }}
    >
      <AlertTitle sx={{ fontSize: '1.125rem', fontWeight: 600, color: colors.dark }}>
        Atenção: {pessoasSemPix.length} pessoa(s) sem chave PIX
      </AlertTitle>

      <Typography variant="body2" sx={{ mb: 2, color: colors.text.secondary }}>
        As seguintes pessoas <strong style={{ color: colors.dark }}>NÃO serão incluídas</strong> no arquivo de pagamento:
      </Typography>

      {pessoasSemPix.map((pessoa) => (
        <Accordion
          key={pessoa.pessoaId}
          sx={{
            mb: 1,
            '&:before': { display: 'none' },
            boxShadow: 'none',
            border: `1px solid ${colors.border}`,
            borderRadius: '12px !important',
            overflow: 'hidden',
            '&:first-of-type': {
              borderRadius: '12px !important',
            },
            '&:last-of-type': {
              borderRadius: '12px !important',
            },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ color: colors.text.secondary }} />}
            sx={{
              backgroundColor: colors.white,
              '&:hover': { backgroundColor: colors.light },
            }}
          >
            <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography 
                variant="subtitle1" 
                sx={{ 
                  fontWeight: 600, 
                  flex: 1,
                  color: colors.dark,
                }}
              >
                {pessoa.pessoa}
              </Typography>
              <Chip
                label={formatarValor(pessoa.valorTotal)}
                size="small"
                sx={{
                  backgroundColor: 'rgba(245, 158, 11, 0.15)',
                  color: '#D97706',
                  fontWeight: 600,
                  fontSize: '0.75rem',
                }}
              />
              <Chip
                label={`${pessoa.quantidadeLancamentos} lançamento(s)`}
                variant="outlined"
                size="small"
                sx={{
                  borderColor: colors.border,
                  color: colors.text.secondary,
                  fontSize: '0.75rem',
                }}
              />
            </Box>
          </AccordionSummary>

          <AccordionDetails sx={{ backgroundColor: colors.light, p: 2 }}>
            <Box>
              <Typography variant="body2" sx={{ mb: 1, color: colors.text.secondary }}>
                <strong style={{ color: colors.dark }}>ID da Pessoa:</strong> {pessoa.pessoaId}
              </Typography>

              <Divider sx={{ my: 2, borderColor: colors.border }} />

              <Typography 
                variant="subtitle2" 
                sx={{ 
                  mb: 1.5, 
                  fontWeight: 600,
                  color: colors.dark,
                }}
              >
                Detalhes dos Lançamentos:
              </Typography>

              <List dense disablePadding>
                {pessoa.lancamentos.map((lanc) => (
                  <ListItem
                    key={lanc.id}
                    sx={{
                      backgroundColor: colors.white,
                      mb: 1,
                      borderRadius: '8px',
                      border: `1px solid ${colors.border}`,
                      px: 2,
                      py: 1,
                    }}
                  >
                    <ListItemText
                      primary={
                        <Typography variant="body2" sx={{ fontWeight: 500, color: colors.dark }}>
                          <strong>{lanc.documento}</strong> - {lanc.descricao}
                        </Typography>
                      }
                      secondary={
                        <Box sx={{ mt: 0.5 }}>
                          <Typography 
                            variant="caption" 
                            display="block"
                            sx={{ color: colors.text.secondary }}
                          >
                            Vencimento: {formatarData(lanc.dataVencimento)}
                          </Typography>
                          <Typography 
                            variant="caption" 
                            display="block"
                            sx={{ color: colors.primary, fontWeight: 600 }}
                          >
                            Valor: {formatarValor(lanc.valor)}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          </AccordionDetails>
        </Accordion>
      ))}

      <Alert 
        severity="info" 
        icon={<InfoIcon />} 
        sx={{ 
          mt: 2,
          borderRadius: '8px',
          backgroundColor: 'rgba(59, 130, 246, 0.08)',
          border: '1px solid rgba(59, 130, 246, 0.2)',
          '& .MuiAlert-icon': {
            color: '#3B82F6',
          },
        }}
      >
        <Typography variant="body2" sx={{ color: colors.text.secondary }}>
          <strong style={{ color: colors.dark }}>Observação:</strong> Solicite o cadastro de chave PIX para estas pessoas
          antes de processar o pagamento.
        </Typography>
      </Alert>
    </Alert>
  );
};

export default AlertaPessoasSemPix;
